import { useEffect, type ReactNode } from "react";

interface ModalProps {
  // Open/close is controlled — the parent owns the state.
  open: boolean;
  onClose: () => void;
  // Maximum content width in px (with a 95vw clamp). Defaults to 500 px,
  // which fits the existing call sites without per-call overrides.
  maxWidth?: number;
  // Optional title for the dialog header. When set we render a header bar
  // with a close (✕) button; otherwise the dialog renders title-less and
  // the caller draws its own header inside `children`.
  title?: string;
  // Stacking order. The top-nav sits around z=50; modals default to 200.
  // Pickers / nested dialogs can bump this when stacking.
  zIndex?: number;
  // Disable the click-scrim-to-close behaviour. Use sparingly for flows
  // where dismissal must be deliberate (e.g. mid-save).
  disableScrimClose?: boolean;
  children: ReactNode;
}

// Modal is the centered-card dialog used across BookingsPage, InvoicesPage,
// and similar flows. It renders a fixed-position scrim + a centered card,
// closes on Escape and on scrim click (unless disabled), and locks scroll
// on the underlying body while open.
//
// Out of scope here: slide-over panels (see InquirySlideOver), top-aligned
// pickers (AssetPicker). Those have different framing and stay bespoke.
export function Modal({
  open,
  onClose,
  maxWidth = 500,
  title,
  zIndex = 200,
  disableScrimClose,
  children,
}: ModalProps) {
  // Escape-to-close. Always called (the hook can't conditionally run);
  // skip the work when closed.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Body scroll-lock so background doesn't scroll under a modal on mobile.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div
        onClick={disableScrimClose ? undefined : onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,.55)",
          zIndex,
        }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: `min(${maxWidth}px, 95vw)`,
          maxHeight: "90vh", overflow: "auto",
          background: "var(--card, var(--surface))",
          borderRadius: "0.75rem",
          padding: title ? 0 : "1.5rem",
          zIndex: zIndex + 1,
          boxShadow: "0 16px 48px rgba(0,0,0,.35)",
          display: "flex", flexDirection: "column",
        }}
      >
        {title && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0.85rem 1.25rem",
            borderBottom: "1px solid var(--border)",
          }}>
            <h2 style={{ margin: 0, fontSize: "1rem" }}>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: "1.1rem", color: "var(--muted)", lineHeight: 1,
                padding: "0 .25rem",
              }}
              aria-label="Close"
            >✕</button>
          </div>
        )}
        <div style={{ padding: title ? "1.25rem" : 0, flex: 1, overflow: "auto" }}>
          {children}
        </div>
      </div>
    </>
  );
}
